import React, { useEffect, useState, useRef, useMemo } from 'react';
import Header from './header';
import LobbySidebar from './LobbySidebar';
import MobileLobbyControls from './MobileLobbyControls';
import { Emitter } from '../../Emitter';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import Room from './Room';
import BattleRoom from './BattleRoom';
import BlackjackRoom from './BlackjackRoom';
import ReconnectOverlay from './ReconnectOverlay';
import { generateSeedHex, commitFromSeed } from '../../utils/fairness';

const RECONNECT_GRACE_SECONDS = 30;
const RECONNECT_ATTEMPT_INTERVAL_MS = 2000;

function GameArea({ player, setPlayer, socket, onLogout, reconnectSocket }) {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [start, setStart] = useState(false);
    const [online, setOnline] = useState(0);
    const [rooms, setRooms] = useState([]);
    const [selectedType, setSelectedType] = useState(() => {
        try {
            const hint = localStorage.getItem('lobby.preferredType');
            if (hint === 'coinflip' || hint === 'battle' || hint === 'blackjack') {
                localStorage.removeItem('lobby.preferredType');
                return hint;
            }
        } catch {
            /* ignore */
        }
        return 'coinflip';
    });
    const [lobbyFilter, setLobbyFilter] = useState('all');
    const emitterRef = useRef(new Emitter());
    const [opponent, setOpponent] = useState(null);
    const [flip, setFlip] = useState(null);
    const [gameBet, setGameBet] = useState(0);
    const [opponentChose, setOpponentChose] = useState(false);
    const [activeGameType, setActiveGameType] = useState(null);
    const [battleState, setBattleState] = useState(null);
    const [battleRound, setBattleRound] = useState(null);
    const [battleEnd, setBattleEnd] = useState(null);
    const [blackjackState, setBlackjackState] = useState(null);
    const [blackjackUpdate, setBlackjackUpdate] = useState(null);
    const [blackjackEnd, setBlackjackEnd] = useState(null);
    const [isReconnecting, setIsReconnecting] = useState(false);
    const [reconnectSecondsLeft, setReconnectSecondsLeft] = useState(RECONNECT_GRACE_SECONDS);
    const [opponentDisconnected, setOpponentDisconnected] = useState(null);
    const [currentRoundId, setCurrentRoundId] = useState(null);
    const [roundReady, setRoundReady] = useState(false);
    const coinflipSeedRef = useRef(null);
    const reconnectingRef = useRef(false);
    const activeGameTypeRef = useRef(null);
    const startRef = useRef(false);
    const opponentCountdownRef = useRef(null);

    useEffect(() => { activeGameTypeRef.current = activeGameType; }, [activeGameType]);
    useEffect(() => { startRef.current = start; }, [start]);

    const changeBet = (e) => setPlayer({ ...player, bet: parseInt(e.target.value) ? parseInt(e.target.value) : 0 });
    const changeIsReady = (status) => setPlayer((prev) => ({ ...prev, isReady: status }));

    const createRoom = async (gameType) => {
        const payload = { id: player.id, bet: player.bet, gameType };
        if (gameType === 'coinflip') {
            const seed = generateSeedHex();
            const commit = await commitFromSeed(seed);
            coinflipSeedRef.current = seed;
            payload.playerCommit = commit;
        }
        socket.send(JSON.stringify({ eventName: 'create.room', payload }));
    };

    useEffect(() => {
        if (player.name == null) {
            navigate('/');
            return;
        }
        const emitter = emitterRef.current;
        emitter.on('online.update', (data) => setOnline(JSON.parse(data)));
        emitter.on('player.update', (data) => setPlayer((prev) => ({ ...prev, ...data })));
        emitter.on('set.flip', (data) => {
            setFlip(data);
            setOpponentChose(false);
            setRoundReady(false);
        });
        emitter.on('round.fairness', (data) => {
            setCurrentRoundId(data.roundId);
            setRoundReady(true);
        });
        emitter.on('next.round', async (data) => {
            setRoundReady(false);
            setCurrentRoundId(null);
            try {
                const seed = generateSeedHex();
                const commit = await commitFromSeed(seed);
                coinflipSeedRef.current = seed;
                socket.send(JSON.stringify({ eventName: 'round.commit', payload: { playerCommit: commit } }));
            } catch (e) {
                console.error('round.commit failed', e);
            }
        });
        emitter.on('round.ready', (data) => {
            setCurrentRoundId(data.roundId);
            setRoundReady(true);
        });
        emitter.on('update.rooms', (data) => setRooms(data));
        emitter.on('notify', ({ status, message, messageKey }) => {
            const text = messageKey ? t(messageKey, { defaultValue: message || messageKey }) : message;
            return status ? toast.success(text) : toast.error(text);
        });
        emitter.on('game.opponent.chose', () => {
            setOpponentChose(true);
        });
        emitter.on('start.game', (data) => {
            setFlip(null);
            setOpponent(data.opponent);
            setGameBet(data.bet);
            setOpponentChose(false);
            setActiveGameType(data.gameType || 'coinflip');
            setBattleState(data.battleState || null);
            setBattleRound(null);
            setBattleEnd(null);
            setBlackjackState(data.blackjackState || null);
            setBlackjackUpdate(null);
            setBlackjackEnd(null);
            setStart(true);
            if ((data.gameType || 'coinflip') === 'coinflip') {
                setRoundReady(false);
                setCurrentRoundId(null);
            }
        });
        emitter.on('battle.round', (data) => {
            setBattleRound(data);
            setOpponentChose(false);
            setBattleState((prev) => prev ? {
                ...prev,
                hp: data.hp,
                round: data.finished ? prev.round : data.round + 1,
                attackerIdx: data.finished ? prev.attackerIdx : (1 - data.attackerIdx),
                attackerName: data.finished ? prev.attackerName : prev.defenderName,
                defenderName: data.finished ? prev.defenderName : prev.attackerName,
            } : prev);
        });
        emitter.on('battle.end', (data) => {
            setBattleEnd(data);
        });
        emitter.on('blackjack.update', (data) => {
            setBlackjackUpdate(data);
            setBlackjackState((prev) => prev ? {
                ...prev,
                hands: data.hands,
                scores: data.scores,
                stood: data.stood,
                busted: data.busted,
            } : prev);
            setOpponentChose(false);
        });
        emitter.on('blackjack.end', (data) => {
            setBlackjackEnd(data);
            setBlackjackState((prev) => prev ? {
                ...prev,
                hands: data.hands,
                scores: data.scores,
                busted: data.busted,
            } : prev);
        });
        emitter.on('stop.game', () => {
            setOpponent(null);
            setStart(false);
            setFlip(null);
            setOpponentChose(false);
            setActiveGameType(null);
            setBattleState(null);
            setBattleRound(null);
            setBattleEnd(null);
            setBlackjackState(null);
            setBlackjackUpdate(null);
            setBlackjackEnd(null);
            setCurrentRoundId(null);
            setRoundReady(false);
            coinflipSeedRef.current = null;
        });
        emitter.on('player.init', (data) => setPlayer((prev) => ({ ...prev, ...data })));
        emitter.on('player.register.fail', () => navigate('/'));
        emitter.on('opponent.disconnected', (data) => {
            if (opponentCountdownRef.current) clearInterval(opponentCountdownRef.current);
            setOpponentDisconnected({ name: data.name, secondsLeft: data.graceSeconds });
            opponentCountdownRef.current = setInterval(() => {
                setOpponentDisconnected((prev) => {
                    if (!prev) return prev;
                    const next = prev.secondsLeft - 1;
                    if (next <= 0) {
                        if (opponentCountdownRef.current) {
                            clearInterval(opponentCountdownRef.current);
                            opponentCountdownRef.current = null;
                        }
                        return { ...prev, secondsLeft: 0 };
                    }
                    return { ...prev, secondsLeft: next };
                });
            }, 1000);
        });
        emitter.on('opponent.reconnected', () => {
            if (opponentCountdownRef.current) {
                clearInterval(opponentCountdownRef.current);
                opponentCountdownRef.current = null;
            }
            setOpponentDisconnected(null);
        });

        // Cleanup — drop all handlers so React 18 strict-mode (or any remount)
        // doesn't pile up duplicate `notify` toasts and double state updates.
        return () => emitter.clear();
        // eslint-disable-next-line
    }, []);

    const attemptReconnect = () => {
        if (reconnectingRef.current) return;
        reconnectingRef.current = true;
        setIsReconnecting(true);
        setReconnectSecondsLeft(RECONNECT_GRACE_SECONDS);

        const startedAt = Date.now();
        let stopped = false;

        const countdown = setInterval(() => {
            const remaining = Math.max(0, RECONNECT_GRACE_SECONDS - Math.floor((Date.now() - startedAt) / 1000));
            setReconnectSecondsLeft(remaining);
        }, 250);

        const finish = (success) => {
            if (stopped) return;
            stopped = true;
            clearInterval(countdown);
            reconnectingRef.current = false;
            setIsReconnecting(false);
            if (!success) {
                toast.error(t('game.forfeitedByDisconnect'));
                setOpponent(null);
                setStart(false);
                setFlip(null);
                setOpponentChose(false);
                setActiveGameType(null);
                setBattleState(null);
                setBattleRound(null);
                setBattleEnd(null);
                setBlackjackState(null);
                setBlackjackUpdate(null);
                setBlackjackEnd(null);
            }
        };

        const tryOnce = () => {
            if (stopped) return;
            if (Date.now() - startedAt >= RECONNECT_GRACE_SECONDS * 1000) {
                finish(false);
                return;
            }
            if (!reconnectSocket) {
                finish(false);
                return;
            }
            reconnectSocket()
                .then(() => finish(true))
                .catch((err) => {
                    if (err && (err.code === 4001 || err.code === 4404)) {
                        finish(false);
                        return;
                    }
                    setTimeout(tryOnce, RECONNECT_ATTEMPT_INTERVAL_MS);
                });
        };

        tryOnce();
    };

    useEffect(() => {
        if (!socket) return;
        socket.onmessage = (message) => {
            const { eventName, payload } = JSON.parse(message.data);
            emitterRef.current.emit(eventName, payload);
        };
        socket.onclose = (event) => {
            if (event.code === 4001) {
                toast.error(t('game.alreadyConnected'));
                navigate('/');
                return;
            }
            if (event.code === 1008 || event.code === 4401 || event.code === 4404) {
                localStorage.removeItem('token');
                navigate('/');
                return;
            }
            const gt = activeGameTypeRef.current;
            if (startRef.current && (gt === 'battle' || gt === 'blackjack') && !reconnectingRef.current) {
                attemptReconnect();
            }
        };
        const sendHello = () => {
            socket.send(JSON.stringify({ eventName: 'player.hello' }));
        };
        if (socket.readyState === WebSocket.OPEN) {
            sendHello();
        } else {
            socket.onopen = sendHello;
        }
    }, [socket]); // eslint-disable-line

    const joinRoom = async (id) => {
        const room = rooms.find(r => r.id === id);
        const payload = { roomId: id };
        if (!room || (room.gameType || 'coinflip') === 'coinflip') {
            const seed = generateSeedHex();
            const commit = await commitFromSeed(seed);
            coinflipSeedRef.current = seed;
            payload.playerCommit = commit;
        }
        socket.send(JSON.stringify({ eventName: 'join.room', payload }));
    };
    const deleteRoom = (id) => socket.send(JSON.stringify({ eventName: 'delete.room', payload: { roomId: id } }));

    const typeCounts = useMemo(() => {
        const counts = { coinflip: 0, battle: 0, blackjack: 0, all: rooms.length };
        for (const r of rooms) {
            const type = r.gameType || 'coinflip';
            if (counts[type] !== undefined) counts[type] += 1;
        }
        return counts;
    }, [rooms]);

    if (start) {
        const overlays = (
            <>
                {isReconnecting && <ReconnectOverlay mode="self" secondsLeft={reconnectSecondsLeft} />}
                {!isReconnecting && opponentDisconnected && (
                    <ReconnectOverlay mode="opponent" name={opponentDisconnected.name} secondsLeft={opponentDisconnected.secondsLeft} />
                )}
            </>
        );
        if (activeGameType === 'battle') {
            return (
                <>
                    <BattleRoom
                        player={player}
                        opponent={opponent}
                        socket={socket}
                        bet={gameBet}
                        battleState={battleState}
                        battleRound={battleRound}
                        battleEnd={battleEnd}
                        opponentChose={opponentChose}
                        setBattleEnd={setBattleEnd}
                        setBattleRound={setBattleRound}
                    />
                    {overlays}
                </>
            );
        }
        if (activeGameType === 'blackjack') {
            return (
                <>
                    <BlackjackRoom
                        player={player}
                        opponent={opponent}
                        socket={socket}
                        bet={gameBet}
                        blackjackState={blackjackState}
                        blackjackUpdate={blackjackUpdate}
                        blackjackEnd={blackjackEnd}
                        setBlackjackUpdate={setBlackjackUpdate}
                    />
                    {overlays}
                </>
            );
        }
        return (
            <Room
                player={player}
                opponent={opponent}
                socket={socket}
                flip={flip}
                setFlip={setFlip}
                changeIsReady={changeIsReady}
                bet={gameBet}
                opponentChose={opponentChose}
                roundReady={roundReady}
                currentRoundId={currentRoundId}
                seedRef={coinflipSeedRef}
            />
        );
    }

    const filteredRooms = lobbyFilter === 'all'
        ? rooms
        : rooms.filter((r) => (r.gameType || 'coinflip') === lobbyFilter);

    return (
        <div className="min-h-screen p-2 sm:p-3 md:p-4">
            <div className="max-w-6xl mx-auto space-y-3 sm:space-y-4">
                <Header player={player} online={online} onLogout={onLogout} />

                <MobileLobbyControls
                    className="lg:hidden"
                    player={player}
                    selectedType={selectedType}
                    setSelectedType={setSelectedType}
                    bet={player.bet}
                    changeBet={changeBet}
                    createRoom={createRoom}
                    lobbyFilter={lobbyFilter}
                    setLobbyFilter={setLobbyFilter}
                    typeCounts={typeCounts}
                />

                <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 sm:gap-4">
                    <div className="hidden lg:block">
                        <LobbySidebar
                            player={player}
                            online={online}
                            selectedType={selectedType}
                            setSelectedType={setSelectedType}
                            bet={player.bet}
                            changeBet={changeBet}
                            createRoom={createRoom}
                            lobbyFilter={lobbyFilter}
                            setLobbyFilter={setLobbyFilter}
                            typeCounts={typeCounts}
                        />
                    </div>

                    <section className="lg:col-span-3 neon-panel p-3 sm:p-4">
                        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                            <h2 className="text-sm sm:text-base font-display font-bold text-white glow-cyan">{t('game.activeRooms')}</h2>
                            <span className="text-[10px] text-cyber-dim tracking-widest">{t('game.lobby', { count: filteredRooms.length })}</span>
                        </div>

                        {filteredRooms.length === 0 ? (
                            <div className="text-center py-8 sm:py-10 text-cyber-dim">
                                <div className="text-3xl sm:text-4xl mb-2 opacity-30">⚡</div>
                                <div className="font-display tracking-widest text-xs sm:text-sm">
                                    {lobbyFilter === 'all' ? t('game.noActiveRooms') : t('game.noRoomsOfType')}
                                </div>
                                <div className="text-[10px] sm:text-xs mt-1">{t('game.deployOneToBegin')}</div>
                            </div>
                        ) : (
                            <ul className="grid gap-2.5 sm:grid-cols-2">
                                {filteredRooms.map((room, index) => {
                                    const isOwner = room.id === player.id;
                                    const isFull = room.players.length >= 2;
                                    const canJoin = !isOwner && !isFull;
                                    const roomGameType = room.gameType || 'coinflip';
                                    const badgeClass =
                                        roomGameType === 'battle' ? 'border-cyber-pink text-cyber-pink'
                                        : roomGameType === 'blackjack' ? 'border-cyber-cyan text-cyber-cyan'
                                        : 'border-cyber-purple text-cyber-purple';
                                    const badgeIcon =
                                        roomGameType === 'battle' ? '⚔'
                                        : roomGameType === 'blackjack' ? '🃏'
                                        : '⚡';
                                    return (
                                        <li key={index} className="neon-panel p-2.5 flex items-center justify-between gap-2 hover:shadow-neon transition">
                                            <div className="flex-1 min-w-0">
                                                <div className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[9px] font-display tracking-widest mb-1 ${badgeClass}`}>
                                                    <span>{badgeIcon}</span>
                                                    <span>{t(`game.type.${roomGameType}`)}</span>
                                                </div>
                                                <div className="neon-label">{t('game.host')}</div>
                                                <div className="text-cyber-cyan glow-cyan font-display tracking-wider truncate text-sm">{room.players[0]}</div>
                                                {room.players[1] && (
                                                    <>
                                                        <div className="neon-label mt-1">{t('game.vs')}</div>
                                                        <div className="text-cyber-pink glow-pink font-display tracking-wider truncate text-sm">{room.players[1]}</div>
                                                    </>
                                                )}
                                            </div>

                                            <div className="text-center px-2 border-x border-cyber-border shrink-0">
                                                <div className="neon-label">{t('game.pot')}</div>
                                                <div className="text-cyber-purple glow-purple font-display font-bold text-base">{room.bet}¢</div>
                                            </div>

                                            <div className="flex flex-col gap-1.5 shrink-0">
                                                {isOwner && (
                                                    <button onClick={() => deleteRoom(room.id)} className="neon-btn neon-btn-pink text-[10px] px-2 py-1">
                                                        {t('game.abort')}
                                                    </button>
                                                )}
                                                {canJoin && (
                                                    <button onClick={() => joinRoom(room.id)} className="neon-btn text-[10px] px-2 py-1">
                                                        {t('game.join')}
                                                    </button>
                                                )}
                                                {isFull && !isOwner && (
                                                    <span className="text-[10px] text-cyber-dim tracking-widest">{t('game.full')}</span>
                                                )}
                                                {isOwner && !isFull && (
                                                    <span className="text-[10px] text-cyber-dim tracking-widest text-center">{t('game.waiting')}</span>
                                                )}
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </section>
                </div>
            </div>
        </div>
    );
}

export default GameArea;
